import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import { Activity, Users, Server, Clock, RefreshCw } from "lucide-react";
import { TID } from "@/lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/server-status`;

const fmt = (v) => (v === null || v === undefined ? "—" : String(v));

export const Stats = () => {
  const [data, setData] = useState({ status: "loading" });
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(API, { cache: "no-store" });
      const j = await r.json();
      setData({ status: "ok", payload: j });
    } catch (e) {
      setData({ status: "error" });
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60_000);
    return () => clearInterval(timerRef.current);
  }, []);

  const loading = data.status === "loading";
  const errored = data.status === "error";
  const ok = data.status === "ok";
  const online = ok && data.payload?.online === true;

  const clients = online ? data.payload?.clients : null;
  const maxClients = online ? data.payload?.max_clients : null;
  const slots =
    online && maxClients !== undefined && maxClients !== null
      ? Math.max(maxClients - (clients ?? 0), 0)
      : null;

  const cards = [
    {
      key: "players",
      label: "Giocatori connessi",
      icon: Users,
      value: clients,
      hint: online ? "Live · API FiveM" : errored ? "API non raggiungibile" : "Server offline",
    },
    {
      key: "slot",
      label: "Slot liberi",
      icon: Server,
      value: slots,
      hint: maxClients ? `su ${maxClients} totali` : "—",
    },
    {
      key: "uptime",
      label: "Stato server",
      icon: Activity,
      value: null,
      text: online ? "ONLINE" : errored ? "ERR" : "OFFLINE",
      tone: online ? "ok" : errored ? "warn" : "off",
      hint: online ? "Connessione attiva" : "In attesa di riapertura",
    },
    {
      key: "ping",
      label: "Ultima sincronizzazione",
      icon: Clock,
      value: null,
      text: ok ? new Date(data.payload.fetched_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—",
      hint: "Aggiornato ogni 60s",
    },
  ];

  return (
    <section
      id="stats"
      data-testid={TID.stats.root}
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#D4AF37]/60" />
                <span className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                  Metriche Server
                </span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-[#F5F5F5] leading-[0.98] tracking-wide">
                Il polso della <span className="text-gold glow-gold">città</span>
              </h2>
            </div>
          </Reveal>
          <Reveal delay={2} className="flex items-center gap-3">
            <button
              onClick={load}
              data-cursor="hot"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] hover:text-[#F5F5F5] transition-colors px-3.5 py-2 rounded-full border border-white/8 hover:border-[#D4AF37]/40"
            >
              <RefreshCw
                size={12}
                className={loading ? "animate-spin" : ""}
              />
              Aggiorna
            </button>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => {
            const Icon = c.icon;
            const showSkeleton = loading;
            const displayVal =
              c.value !== null && c.value !== undefined ? c.value : c.text ?? "—";
            return (
              <Reveal key={c.key} delay={Math.min(i + 1, 5)}>
                <div
                  data-testid={TID.stats.card(c.key)}
                  className="card-wow rounded-2xl p-7 h-full"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-[#0B0B0F]/60 border border-[#D4AF37]/25 flex items-center justify-center">
                      <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.6} />
                    </div>
                    {c.tone && (
                      <span
                        className={`text-[9px] tracking-[0.28em] uppercase px-2.5 py-1 rounded-full border ${
                          c.tone === "ok"
                            ? "border-[#00D4FF]/40 text-[#00D4FF] bg-[#00D4FF]/5"
                            : c.tone === "warn"
                              ? "border-amber-400/40 text-amber-300 bg-amber-400/5"
                              : "border-red-500/40 text-red-400 bg-red-500/5"
                        }`}
                      >
                        {c.tone === "ok" ? "Live" : c.tone === "warn" ? "Errore" : "Offline"}
                      </span>
                    )}
                  </div>
                  <div className="mt-10">
                    {showSkeleton ? (
                      <div className="h-12 w-24 rounded-md bg-white/5 animate-pulse" />
                    ) : (
                      <div className="font-display text-5xl text-[#F5F5F5] leading-none truncate">
                        {displayVal === "—" ? (
                          <span className="text-[#F5F5F5]/30">—</span>
                        ) : (
                          displayVal
                        )}
                      </div>
                    )}
                    <div className="mt-5 text-sm font-bold text-[#F5F5F5]">
                      {c.label}
                    </div>
                    <div className="mt-1 text-xs text-[#B6B6BE]">{c.hint}</div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={3} className="mt-6 text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE]">
          Dati forniti dall&apos;API ufficiale FiveM · server code{" "}
          <span className="text-[#D4AF37] font-mono-pl">4gbraqq</span>
        </Reveal>
      </div>
    </section>
  );
};

export default Stats;
